import React, { useState, useEffect } from 'react';
import { useSchool } from '../context/SchoolContext';
import { DollarSign, X, User, Calendar, Tag, FileText, ChevronDown, CheckCircle } from 'lucide-react';
import './FinancialModal.css';

const RecordPaymentModal = ({ isOpen, onClose, paymentToEdit = null }) => {
    const { recordPayment, updatePayment, customers = [], paymentMethods = [], branches = [] } = useSchool();
    const isEditing = !!paymentToEdit;

    const [paymentData, setPaymentData] = useState({
        customerId: '',
        amount: '',
        method: 'Cash',
        description: '',
        date: new Date().toISOString().split('T')[0],
        branch_id: ''
    });

    useEffect(() => {
        if (isOpen) {
            if (paymentToEdit) {
                setPaymentData({
                    customerId: paymentToEdit.customer_id || paymentToEdit.customerId || '',
                    amount: paymentToEdit.amount || '',
                    method: paymentToEdit.method || 'Cash',
                    description: paymentToEdit.description || '',
                    date: (paymentToEdit.payment_date || paymentToEdit.date || '').split('T')[0] || new Date().toISOString().split('T')[0],
                    branch_id: paymentToEdit.branch_id || ''
                });
            } else {
                setPaymentData({
                    customerId: '',
                    amount: '',
                    method: paymentMethods[0] || 'Cash',
                    description: '',
                    date: new Date().toISOString().split('T')[0],
                    branch_id: ''
                });
            }
        }
    }, [isOpen, paymentToEdit, paymentMethods]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setPaymentData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const finalData = {
            ...paymentData,
            amount: parseFloat(paymentData.amount)
        };

        if (isEditing) {
            updatePayment(paymentToEdit.id, finalData);
        } else {
            recordPayment(finalData);
        }
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="f-modal-overlay">
            <div className="f-modal-content">
                <div className="f-modal-header">
                    <div className="f-header-info">
                        <div className="f-icon-box income">
                            <DollarSign size={24} />
                        </div>
                        <div>
                            <h3 className="f-modal-title">{isEditing ? 'Edit Payment' : 'Record Payment'}</h3>
                            <p className="text-secondary text-sm">{isEditing ? 'Update the details of this income record' : 'Log income from a student'}</p>
                        </div>
                    </div>
                    <button className="f-close-btn" onClick={onClose}><X size={20} /></button>
                </div>

                <form onSubmit={handleSubmit} className="f-modal-form">
                    <div className="f-form-grid">
                        <div className="f-field-group f-field-full">
                            <label className="f-label">
                                <User size={14} /> STUDENT / CUSTOMER
                            </label>
                            <div className="f-input-wrapper">
                                <User className="f-input-icon" size={18} />
                                <select
                                    name="customerId"
                                    className="f-input f-select"
                                    required
                                    value={paymentData.customerId}
                                    onChange={handleInputChange}
                                >
                                    <option value="">Select Student...</option>
                                    {customers.map(c => (
                                        <option key={c.id} value={c.id}>{c.name || `${c.first_name} ${c.last_name}`}</option>
                                    ))}
                                </select>
                                <ChevronDown className="f-chevron" size={18} />
                            </div>
                        </div>

                        <div className="f-field-group">
                            <label className="f-label">
                                <Tag size={14} /> AMOUNT ($)
                            </label>
                            <div className="f-input-wrapper">
                                <DollarSign className="f-input-icon" size={18} />
                                <input
                                    type="number"
                                    name="amount"
                                    className="f-input f-amount-p"
                                    placeholder="0.00"
                                    step="0.01"
                                    required
                                    value={paymentData.amount}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>

                        <div className="f-field-group">
                            <label className="f-label">
                                <CheckCircle size={14} /> METHOD
                            </label>
                            <div className="f-input-wrapper">
                                <FileText className="f-input-icon" size={18} />
                                <select
                                    name="method"
                                    className="f-input f-select"
                                    required
                                    value={paymentData.method}
                                    onChange={handleInputChange}
                                >
                                    {paymentMethods.map(m => (
                                        <option key={m} value={m}>{m}</option>
                                    ))}
                                </select>
                                <ChevronDown className="f-chevron" size={18} />
                            </div>
                        </div>

                        <div className="f-field-group f-field-full">
                            <label className="f-label">
                                <Calendar size={14} /> DATE OF PAYMENT
                            </label>
                            <div className="f-input-wrapper">
                                <Calendar className="f-input-icon" size={18} />
                                <input
                                    type="date"
                                    name="date"
                                    className="f-input"
                                    required
                                    value={paymentData.date}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>

                        <div className="f-field-group f-field-full">
                            <label className="f-label">
                                <Tag size={14} /> BRANCH
                            </label>
                            <div className="f-input-wrapper">
                                <Tag className="f-input-icon" size={18} />
                                <select
                                    name="branch_id"
                                    className="f-input f-select"
                                    value={paymentData.branch_id}
                                    onChange={handleInputChange}
                                >
                                    <option value="">-- No Branch Assiged / Global --</option>
                                    {branches.map(b => (
                                        <option key={b.id} value={b.id}>{b.branch_name}</option>
                                    ))}
                                </select>
                                <ChevronDown className="f-chevron" size={18} />
                            </div>
                        </div>

                        <div className="f-field-group f-field-full">
                            <label className="f-label">
                                <FileText size={14} /> DESCRIPTION / NOTES
                            </label>
                            <div className="f-input-wrapper">
                                <FileText className="f-input-icon" style={{ top: '1rem' }} size={18} />
                                <textarea
                                    name="description"
                                    className="f-input f-textarea"
                                    placeholder="e.g. Payment for full package lessons..."
                                    rows="3"
                                    value={paymentData.description}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="f-modal-footer">
                        <button type="button" className="f-btn f-btn-secondary" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="f-btn f-btn-primary">
                            <CheckCircle size={18} /> {isEditing ? 'Update Payment' : 'Record Payment'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RecordPaymentModal;
